import React from 'react';
import { Link } from 'react-router-dom';
import { Award, BookOpen, Users, Target, ArrowRight, CheckCircle, FileText, Lightbulb } from 'lucide-react';

const RegulatoryGuidanceService = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Banner Section */}
      <section className="relative text-white py-20 md:py-32 overflow-hidden">
        {/* Responsive Background Image */}
        <picture className="absolute inset-0 w-full h-full">
          <source 
            srcSet="/images/hero/reg-guidance/desktop_2560x1200.webp" 
            media="(min-width: 1440px)" 
            type="image/webp"
          />
          <source 
            srcSet="/images/hero/reg-guidance/tablet_1536x900.webp" 
            media="(min-width: 768px)" 
            type="image/webp"
          />
          <source 
            srcSet="/images/hero/reg-guidance/mobile_horiz_1200x960.webp" 
            media="(max-width: 767px) and (orientation: landscape)" 
            type="image/webp"
          />
          <source 
            srcSet="/images/hero/reg-guidance/mobile_vert_960x1200.webp" 
            media="(max-width: 767px) and (orientation: portrait)" 
            type="image/webp"
          />
          <img 
            src="/images/hero/reg-guidance/desktop_2560x1200.webp"
            alt="Aerial view of a forest road used as hero banner for Raven Environmental Regulatory Guidance services."
            loading="eager"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </picture>
        
        {/* Dark Overlay for Text Readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl text-left mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight drop-shadow-2xl" style={{ textShadow: '2px 2px 8px rgba(0, 0, 0, 0.9)' }}>
              Regulatory Guidance & Environmental Compliance
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-50 leading-relaxed drop-shadow-lg" style={{ textShadow: '1px 1px 6px rgba(0, 0, 0, 0.8)' }}>
              Supporting agencies, communities, and industry in navigating complex environmental regulations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/regulatory-support"
                className="inline-flex items-center justify-center px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105"
              >
                Get Expert Guidance
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Regulatory Guidance Matters
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Environmental regulations are complex, constantly changing, and costly to misunderstand
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-left">
              <div className="bg-gradient-to-br from-red-50 to-orange-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Avoid Mistakes</h3>
              <p className="text-gray-600">
                Prevent costly misinterpretations that lead to violations and penalties
              </p>
            </div>

            <div className="text-left">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lightbulb className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Strategic Planning</h3>
              <p className="text-gray-600">
                Make informed business decisions based on regulatory requirements
              </p>
            </div>

            <div className="text-left">
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Stay Updated</h3>
              <p className="text-gray-600">
                Keep pace with new rules, guidance documents, and enforcement trends
              </p>
            </div>

            <div className="text-left">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Access</h3>
              <p className="text-gray-600">
                Direct access to consultants with decades of regulatory experience
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Regulatory Areas */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Regulatory Areas We Cover
              </h2>
              <div className="space-y-4">
                {[
                  { area: 'Clean Air Act', detail: 'Federal and state air quality regulations, NAAQS, HAPs, and NSPS' },
                  { area: 'Alaska DEC Requirements', detail: 'State-specific air quality standards and permitting requirements' },
                  { area: 'Title V Operating Permits', detail: 'Major source operating permit requirements and compliance' },
                  { area: 'New Source Review', detail: 'PSD and NSR requirements for new and modified sources' },
                  { area: 'Emission Standards', detail: 'Technology-based and health-based emission standards' },
                  { area: 'Monitoring & Testing', detail: 'CEMS, stack testing, and monitoring requirements' },
                  { area: 'Recordkeeping & Reporting', detail: 'Documentation and reporting obligations' },
                  { area: 'Enforcement Actions', detail: 'Understanding notices of violation and consent decrees' }
                ].map((item, index) => (
                  <div key={index} className="bg-white p-5 rounded-lg shadow-sm">
                    <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                      <CheckCircle className="w-5 h-5 text-indigo-600 mr-2" />
                      {item.area}
                    </h4>
                    <p className="text-sm text-gray-600 ml-7">{item.detail}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                How We Help You Navigate
              </h2>
              
              <div className="bg-white p-8 rounded-xl shadow-lg mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Our Guidance Services</h3>
                <div className="space-y-6">
                  {[
                    { service: 'Regulatory Interpretation', desc: 'Plain-language explanations of complex requirements' },
                    { service: 'Applicability Determinations', desc: 'Determine which regulations apply to your operations' },
                    { service: 'Compliance Strategy', desc: 'Develop cost-effective approaches to meet requirements' },
                    { service: 'Agency Interaction', desc: 'Interface with regulators on your behalf' },
                    { service: 'Regulatory Tracking', desc: 'Monitor new rules and assess impacts to your facility' },
                    { service: 'Training & Education', desc: 'Staff training on relevant regulatory requirements' }
                  ].map((item, index) => (
                    <div key={index} className="pb-4 border-b border-gray-200 last:border-0">
                      <h4 className="font-semibold text-gray-900 mb-1">{item.service}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-xl border-2 border-amber-200">
                <Award className="w-10 h-10 text-amber-600 mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  13+ Years of Regulatory Expertise
                </h3>
                <p className="text-gray-700">
                  Our team has worked directly with Alaska DEC and EPA on hundreds of regulatory matters, giving us unique insight into regulatory interpretation and enforcement priorities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Savings */}
      <section className="py-16 bg-gradient-to-br from-indigo-900 to-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-12">
            <h2 className="text-3xl font-bold mb-4">The Value of Expert Guidance</h2>
            <p className="text-xl text-indigo-100">
              Understanding the ROI of regulatory consultation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl text-left">
              <div className="text-4xl font-bold text-amber-400 mb-2">$50K-500K</div>
              <h3 className="text-xl font-semibold mb-3">Violation Prevention</h3>
              <p className="text-indigo-100">
                Average cost of regulatory violations that can be prevented with expert guidance
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl text-left">
              <div className="text-4xl font-bold text-amber-400 mb-2">40-60%</div>
              <h3 className="text-xl font-semibold mb-3">Project Savings</h3>
              <p className="text-indigo-100">
                Typical savings on capital projects through optimized regulatory strategies
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl text-left">
              <div className="text-4xl font-bold text-amber-400 mb-2">Months</div>
              <h3 className="text-xl font-semibold mb-3">Time Saved</h3>
              <p className="text-indigo-100">
                Avoid delays from regulatory misunderstandings or incorrect approaches
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-left">
            Regulatory Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'EPA Regulations', desc: 'Federal environmental regulations and guidance', link: 'https://www.epa.gov/laws-regulations' },
              { title: 'Alaska DEC', desc: 'State environmental regulations and permits', link: 'https://dec.alaska.gov/' },
              { title: 'CFR Title 40', desc: 'Code of Federal Regulations - Environmental Protection', link: 'https://www.ecfr.gov/current/title-40' }
            ].map((resource, index) => (
              <a 
                key={index}
                href={resource.link}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-br from-gray-50 to-indigo-50 p-6 rounded-xl hover:shadow-lg transition-shadow"
              >
                <FileText className="w-8 h-8 text-indigo-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{resource.title}</h3>
                <p className="text-gray-600 mb-4">{resource.desc}</p>
                <span className="text-indigo-600 font-semibold">View Resource →</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-purple-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
          <h2 className="text-3xl font-bold mb-4">
            Get Clear Regulatory Answers Today
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Stop guessing—get expert guidance from Alaska's environmental compliance specialists
          </p>
          <Link
            to="/regulatory-support"
            className="inline-flex items-center px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg text-lg"
          >
            Schedule Your Consultation
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default RegulatoryGuidanceService;